package com.elan.edubridge.ui.shell

/**
 * `/` — 로그인하지 않은 방문자 전용(가이드 §3.1). 인증 여부 판정·리다이렉트는 라우트 등록
 * 시점에 [publicOnly]가 감싸므로(Main.kt) 이 파일은 항상 익명 방문자 기준으로만 그린다.
 *
 * 앱은 둘이다(APP_SPLIT_PLAN_v1.0.0): 학생·보호자용 [STUDENT_APP_NAME](Google Play 게시 중)과
 * 원장·선생님용 [MANAGER_APP_NAME](심사 중). 스토어 링크와 배지 상태는 [StoreLinks] 한 곳이 정한다.
 * 기능 문구는 두 앱의 스토어 설명(`apps/android/{,manager/}fastlane/metadata/**/full_description.txt`)과
 * 같은 사실만 말한다 — 화면에 없는 기능을 적으면 리스팅과 웹이 어긋난다. 기능 그림도 스톡 사진이 아니라
 * 실제 앱 캡처를 합성한 것이다(`tools/store-assets/make_web_images.py`).
 */
object LandingPage {
    fun render() {
        renderPage("eb-landing", html())
    }

    /** 히어로 CTA. 학생·보호자 앱 스토어가 있으면 그리로, 없으면 회원가입으로. */
    private fun heroStoreCta(): String = StoreLinks.studentPlay?.let {
        """<a class="eb-button eb-button--primary eb-button--lg" href="${it.escapeHtml()}" target="_blank" rel="noopener">${icon("icon-play", "sm")}Google Play에서 앱 받기</a>"""
    } ?: """<a class="eb-button eb-button--primary eb-button--lg" href="/signup">회원가입</a>"""

    private fun html(): String = """
        <header class="eb-landing-nav">
            <a class="eb-landing-nav__brand" href="/">${icon("icon-bridge-logo", "lg")}<span>EduBridge</span></a>
            <nav class="eb-landing-nav__links" aria-label="주 메뉴">
                <a href="#features">기능</a><a href="#app-download">앱 다운로드</a><a href="#trust">알림 방식</a><a href="/guide">이용 가이드</a>
            </nav>
            <div class="eb-landing-nav__actions">
                <a class="eb-button eb-button--ghost" href="/login">로그인</a>
                <a class="eb-button eb-button--primary" href="/signup">회원가입</a>
            </div>
        </header>

        <section class="eb-hero">
            <div class="eb-hero__copy">
                <h1 class="eb-hero__title">오늘도 안심할 수 있도록</h1>
                <p class="eb-hero__subtitle">등원부터 하원까지, 아이의 학원 생활을<br>가장 따뜻하고 정확하게 전합니다.</p>
                <div class="eb-hero__ctas">
                    ${heroStoreCta()}
                    <a class="eb-button eb-button--ghost eb-button--lg" href="/guide">학원 운영 시작하기</a>
                </div>
            </div>
            <div class="eb-hero__media">
                <img src="/images/hero-parent.jpg?v=1" alt="" role="presentation" class="eb-hero__photo">
                <div class="eb-hero__toast" role="note">
                    ${icon("icon-notification", "md")}
                    <div><strong>김하늘 학생이</strong><span class="eb-meta">지금</span><p>오후 6:10에 하원했습니다.</p></div>
                    ${icon("icon-check", "sm", "eb-text-success")}
                </div>
            </div>
        </section>

        <section class="eb-features" id="features" aria-label="핵심 기능">
            <article class="eb-feature-card">
                <span class="eb-feature-card__icon eb-feature-card__icon--success">${icon("icon-attendance", "xl")}</span>
                <h3>빠른 출결</h3><p>QR 한 번으로 등원·하원이 기록돼요. 선생님은 명단에서 출석·지각·결석을 한 번에 처리해요.</p>
                <a class="eb-feature-card__more" href="#feature-attendance">자세히 보기 ${icon("icon-arrow-right", "xs")}</a>
            </article>
            <article class="eb-feature-card">
                <span class="eb-feature-card__icon eb-feature-card__icon--rose">${icon("icon-notification", "xl")}</span>
                <h3>출결 푸시 알림</h3><p>기록되는 순간 보호자에게, 그리고 선생님에게도 푸시 알림으로 전해요.</p>
                <a class="eb-feature-card__more" href="#feature-notify">자세히 보기 ${icon("icon-arrow-right", "xs")}</a>
            </article>
            <article class="eb-feature-card">
                <span class="eb-feature-card__icon eb-feature-card__icon--amber">${icon("icon-academy", "xl")}</span>
                <h3>학원 운영</h3><p>학생·반, 서식 공지, 과제와 자료, 기간별 통계까지 관리자 앱과 PC 웹 콘솔에서 한곳에.</p>
                <a class="eb-feature-card__more" href="#feature-admin">자세히 보기 ${icon("icon-arrow-right", "xs")}</a>
            </article>
        </section>

        <section class="eb-feature-details" aria-label="기능 상세 설명">
            <article class="eb-feature-detail" id="feature-attendance">
                <div class="eb-feature-detail__media">
                    <img src="/images/feature-attendance-v1.png?v=1" alt="관리자 앱의 출결 QR 키오스크 화면과, 등원이 기록된 학생 앱 홈 화면">
                </div>
                <div class="eb-feature-detail__copy">
                    <span class="eb-feature-detail__eyebrow eb-text-success">빠른 출결</span>
                    <h2>QR 코드 스캔 한 번으로 끝나는 출결</h2>
                    <p>학원에 띄워 둔 QR 키오스크를 EduBridge 앱으로 스캔하면 등원·하원이 즉시 기록됩니다.
                    QR은 주기적으로 새로 발급되는 일회용이라 화면을 찍어 보내는 방식으로는 출석이 되지 않고,
                    선생님은 관리자 앱에서 반별 명단을 보며 출석·지각·결석을 직접 처리하거나 잘못된 기록을 정정할 수 있습니다.</p>
                </div>
            </article>
            <article class="eb-feature-detail eb-feature-detail--reverse" id="feature-notify">
                <div class="eb-feature-detail__media">
                    <img src="/images/feature-push-attendance.png?v=1" alt="보호자 휴대폰에 등원·하원 출결 푸시 알림이 도착한 모습">
                </div>
                <div class="eb-feature-detail__copy">
                    <span class="eb-feature-detail__eyebrow eb-text-error">출결 푸시 알림</span>
                    <h2>등원·하원 소식, 그 순간 푸시 알림으로</h2>
                    <p>아이가 학원에 도착하거나 나서는 순간, 연결된 보호자 모두에게 푸시 알림이 갑니다.
                    보호자는 초대를 수락해야만 자녀와 연결되고, 전화번호가 같다는 이유로 자동 연결되는 일은 없습니다.
                    원장님과 선생님도 관리자 앱으로 같은 출결 알림을 받습니다.</p>
                </div>
            </article>
            <article class="eb-feature-detail" id="feature-admin">
                <div class="eb-feature-detail__media">
                    <img src="/images/feature-ops-v1.png?v=1" alt="관리자 앱의 오늘 대시보드와 기간별 출결 통계 화면">
                </div>
                <div class="eb-feature-detail__copy">
                    <span class="eb-feature-detail__eyebrow eb-text-warning">학원 운영</span>
                    <h2>학원 운영, 앱과 PC 어디서든</h2>
                    <p>오늘 수업과 등원·하원·미등원 인원, 학생이 QR을 스캔하는 순간의 실시간 출결 피드를 한 화면에서 봅니다.
                    학생·반 관리와 초대, 가입 신청 승인, 굵게·제목·목록이 들어간 서식 공지, PDF·사진 자료가 붙은 과제,
                    기간별 반·학생 출결 통계까지 관리자 앱과 웹 콘솔이 같은 학원 데이터를 다룹니다.</p>
                </div>
            </article>
        </section>

        <section class="eb-app-download" id="app-download" aria-labelledby="app-download-title">
            <h2 id="app-download-title">역할에 맞는 앱을 받으세요</h2>
            <p class="eb-meta">학생·보호자 앱과 관리자 앱은 같은 학원 데이터를 씁니다. 같은 계정으로 PC 웹에서도 이용할 수 있어요.</p>
            <div class="eb-app-cards">
                <article class="eb-app-card" aria-labelledby="app-card-student">
                    <div class="eb-app-card__head">
                        <img class="eb-app-card__icon" src="/images/app-icon-student.png?v=1" alt="">
                        <div><span class="eb-app-card__role">학생·보호자용</span><h3 id="app-card-student">$STUDENT_APP_NAME</h3></div>
                    </div>
                    <ul>
                        <li>QR 한 번으로 등원·하원 기록</li>
                        <li>기록되는 순간 보호자에게 푸시 알림</li>
                        <li>공지·게시판, 과제와 자료(PDF·사진)</li>
                        <li>출결 통계와 수업 일정</li>
                    </ul>
                    ${studentAppBadges()}
                </article>
                <article class="eb-app-card" aria-labelledby="app-card-manager">
                    <div class="eb-app-card__head">
                        <img class="eb-app-card__icon" src="/images/app-icon-manager.png?v=1" alt="">
                        <div><span class="eb-app-card__role">원장·선생님용</span><h3 id="app-card-manager">$MANAGER_APP_NAME</h3></div>
                    </div>
                    <ul>
                        <li>오늘 수업·등원·하원·미등원과 실시간 출결 피드</li>
                        <li>출결 처리, 태블릿용 QR 키오스크</li>
                        <li>학생·반 관리, 초대 발급, 가입 신청 승인</li>
                        <li>서식 공지, 과제·자료 배포, 기간별 통계</li>
                        <li>학원 개설 신청과 Google Play 구독(7일 무료 체험)</li>
                    </ul>
                    ${managerAppBadges()}
                    <p class="eb-meta">PC에서는 <a href="/login">웹 콘솔</a>로 같은 학원을 관리할 수 있어요.</p>
                </article>
            </div>
            <p class="eb-meta">웹에서도 등원·하원, 공지·과제와 알림함을 확인할 수 있습니다. <a href="/signup">회원가입</a> · <a href="/guide">이용 가이드</a></p>
        </section>

        <section class="eb-trust-banner" id="trust">
            ${icon("icon-shield", "xl")}
            <h2>기록은 정확하게, 알림은 다정하게.</h2>
            <p>EduBridge는 안전한 시스템과 엄격한 보안 정책으로 신뢰를 지킵니다. 공지·게시판·과제 자료는 같은 학원 구성원에게만 보이고, 아이가 쓰는 앱에는 광고를 넣지 않았습니다.</p>
        </section>

        ${publicFooter()}
    """.trimIndent()
}
